import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loginUser } from '@/hooks/useAppStore';
import {
  loginWithEmail,
  loginWithPhone,
  sendEmailLoginCode,
  sendPhoneLoginCode,
  getAuthCapabilities,
  type AuthCapabilities,
  type LoginResponse,
} from '@/api/auth';
import { setTokens, ApiError } from '@/api/client';
import AgreementSheet from '@/components/feature/AgreementSheet';
import { useAppConfig } from '@/hooks/useAppConfig';
import { getMinPointsCost } from '@/api/config';
import { getCurrentLang } from '@/i18n/index';
import { getDeviceInfo } from '@/utils/deviceInfo';
import { APP_BRAND_MARK_URL } from '@/config/brand';
import { track } from '@/utils/tracker';
import {
  PHONE_COUNTRIES,
  isNationalNumberValid,
  normalizeNationalNumber,
  toE164Phone,
} from './phoneCountries';

type LoginMode = 'phone' | 'email';
const TERMS_KEY = 'gp_terms_v1';

export default function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const config = useAppConfig();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';

  const lang = getCurrentLang();
  const isZh = lang === 'zh';

  const registerGift = config?.points_rules?.register_gift;
  const minCost = getMinPointsCost(config);
  const maxImages = (registerGift && minCost) ? Math.floor(registerGift / minCost) : 0;

  const [loginMode, setLoginMode] = useState<LoginMode>(isZh ? 'phone' : 'email');

  const [countryCode, setCountryCode] = useState('CN');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [agreed, setAgreed] = useState(() => localStorage.getItem(TERMS_KEY) === '1');
  const [viewAgreementKey, setViewAgreementKey] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [challengeId, setChallengeId] = useState('');
  const [capabilities, setCapabilities] = useState<AuthCapabilities | null>(null);

  const enabledCountries = PHONE_COUNTRIES.filter((country) =>
    capabilities?.phone_country_codes.includes(country.code),
  );
  const selectedCountry = enabledCountries.find((country) => country.code === countryCode)
    ?? enabledCountries[0]
    ?? PHONE_COUNTRIES[0];

  useEffect(() => {
    void getAuthCapabilities()
      .then((result) => {
        setCapabilities(result);
        setCountryCode((current) => result.phone_country_codes.includes(current)
          ? current
          : result.phone_country_codes[0] ?? 'CN');
        setLoginMode((current) => current === 'phone' && result.phone_country_codes.length === 0
          ? 'email'
          : current);
      })
      .catch(() => setCapabilities({
        providers: ['email_otp'],
        phone_country_codes: [],
        google_enabled: false,
        apple_enabled: false,
      }));
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [countdown]);

  const isPhoneValid = isNationalNumberValid(phone, selectedCountry);
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const currentIdentifierValid = loginMode === 'phone' ? isPhoneValid : isEmailValid;

  const handleSendCode = async () => {
    if (!currentIdentifierValid) {
      setError(loginMode === 'phone' ? t('login_errPhone') : t('login_errEmail'));
      return;
    }
    if (!agreed) {
      setError(t('login_errAgree'));
      return;
    }
    setError('');
    setCodeLoading(true);
    try {
      if (loginMode === 'phone') {
        const res = await sendPhoneLoginCode(toE164Phone(phone, selectedCountry));
        setChallengeId(res.challenge_id);
        setCodeSent(true);
        setCountdown(res.rate_seconds ?? 60);
      } else {
        const res = await sendEmailLoginCode(email.trim().toLowerCase());
        setChallengeId(res.challenge_id);
        setCodeSent(true);
        setCountdown(res.rate_seconds ?? 60);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'AUTH_004') {
          setCountdown(60);
          setError(err.message);
        } else if (err.code === 'SYS_002' || err.code === 'SYS_003') {
          setError(t('login_errSmsFail'));
        } else {
          setError(err.message);
        }
      } else {
        setError(t('login_errSendFail'));
      }
    } finally {
      setCodeLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!currentIdentifierValid) {
      setError(loginMode === 'phone' ? t('login_errPhone') : t('login_errEmail'));
      return;
    }
    if (!code || code.length !== 6) {
      setError(loginMode === 'phone' ? t('login_errCode') : t('login_errEmailCode'));
      return;
    }
    if (!challengeId) {
      setError(t('login_errChallenge'));
      return;
    }
    if (!agreed) {
      setError(t('login_errAgree'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      const deviceInfo = getDeviceInfo();
      const commonPayload = {
        challenge_id: challengeId,
        otp: code,
        device_id: deviceInfo.device_id,
        device_info: deviceInfo,
        agreed_terms: agreed,
      };
      const data = loginMode === 'phone'
        ? await loginWithPhone({
          ...commonPayload,
          phone: toE164Phone(phone, selectedCountry),
        })
        : await loginWithEmail({
          ...commonPayload,
          email: email.trim().toLowerCase(),
        });
      await completeLogin(data, loginMode);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t('login_errLoginFail'));
      }
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = async (data: LoginResponse, method: LoginMode) => {
    await setTokens(data.access_token, data.refresh_token, data.expires_in);
    loginUser(
      {
        userId: data.user.user_id,
        nickname: data.user.nickname,
        avatar: data.user.avatar,
        phone: data.user.phone ?? undefined,
        email: data.user.email ?? undefined,
        points: data.user.points,
        isVip: data.user.is_vip,
        vipExpireTime: data.user.vip_expire_time,
      },
      { accessToken: data.access_token, refreshToken: data.refresh_token },
    );
    track.loginSuccess(method);
    navigate(redirect, { replace: true });
  };

  const handleSwitchMode = (mode: LoginMode) => {
    setLoginMode(mode);
    setPhone('');
    setEmail('');
    setCode('');
    setError('');
    setCodeSent(false);
    setChallengeId('');
    setCountdown(0);
  };

  const toggleAgreement = () => {
    setAgreed((current) => {
      const next = !current;
      if (next) {
        localStorage.setItem(TERMS_KEY, '1');
      } else {
        localStorage.removeItem(TERMS_KEY);
      }
      return next;
    });
    setError('');
  };

  const sendBtnText = codeLoading
    ? t('login_sending')
    : countdown > 0
    ? `${countdown}s`
    : t(loginMode === 'phone' ? 'login_sendCode' : 'login_sendEmailCode');

  return (
    <div className="min-h-screen flex flex-col font-nunito overflow-x-hidden" style={{ background: 'linear-gradient(160deg, #FFF0F6 0%, #F5EEFF 50%, #FFF0F6 100%)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* 顶部装饰 */}
      <div className="relative overflow-hidden pb-8 px-6 flex flex-col items-center" style={{ paddingTop: 'calc(2rem + env(safe-area-inset-top))' }}>
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-20"
          style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', transform: 'translate(30%, -30%)' }}></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full opacity-15"
          style={{ background: 'linear-gradient(135deg, #A855F7, #FF6B9D)', transform: 'translate(-30%, 30%)' }}></div>

        <div className="relative z-10 flex flex-col items-center">
          <div className="w-[4.5rem] h-[4.5rem] rounded-3xl flex items-center justify-center bg-white overflow-hidden mb-4 shadow-sm">
            <img src={APP_BRAND_MARK_URL} alt="logo" className="w-14 h-14 object-contain rounded-2xl" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('login_title')}</h1>
          <p className="text-sm text-gray-500 mt-1 font-medium">{t('login_subtitle')}</p>
        </div>
      </div>

      {/* 主文案 */}
      <div className="px-8 text-center mb-8">
        <p className="text-xl font-bold text-gray-800 leading-snug">{t('login_slogan1')}</p>
        <p className="text-xl font-bold leading-snug" style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          {t('login_slogan2')}
        </p>
        {registerGift !== undefined && (
          <p className="text-sm text-gray-400 mt-2">{t('newUserGift_desc', { giftPoints: registerGift, maxImages })}</p>
        )}
      </div>

      {/* 表单卡片 */}
      <div className="flex-1 px-5">
        <div className="bg-white rounded-3xl p-6 shadow-sm overflow-hidden">

          {/* 手机号输入（中文手机模式） */}
          {loginMode === 'phone' && (
            <div className="mb-4">
              <label className="text-xs font-semibold text-gray-500 mb-2 block">{t('login_phoneLabel')}</label>
              <div className="flex items-center bg-gray-50 rounded-2xl px-4 h-12">
                <select
                  data-testid="phone-country-select"
                  aria-label="consumer-phone-country"
                  value={countryCode}
                  onChange={(event) => {
                    setCountryCode(event.target.value);
                    setPhone('');
                    setCode('');
                    setChallengeId('');
                    setCodeSent(false);
                    setCountdown(0);
                  }}
                  className="max-w-[7.5rem] bg-transparent text-sm text-gray-600 outline-none"
                >
                  {enabledCountries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.code} {country.dialCode}
                    </option>
                  ))}
                </select>
                <div className="w-px h-4 bg-gray-200 mr-3"></div>
                <input
                  data-testid="phone-input"
                  aria-label="consumer-phone-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(normalizeNationalNumber(e.target.value, selectedCountry))}
                  placeholder={t('login_phonePlaceholder')}
                  className="flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder-gray-400"
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          {/* 邮箱输入（邮箱模式） */}
          {loginMode === 'email' && (
            <div className="mb-4">
              <label className="text-xs font-semibold text-gray-500 mb-2 block">{t('login_emailLabel')}</label>
              <div className="flex items-center bg-gray-50 rounded-2xl px-4 h-12">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-mail-line text-gray-400 text-base"></i>
                </div>
                <input
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('login_emailPlaceholder')}
                  className="flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder-gray-400"
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          {/* 验证码 */}
          <div className="mb-5">
            <label className="text-xs font-semibold text-gray-500 mb-2 block">
              {loginMode === 'phone' ? t('login_codeLabel') : t('login_emailCodeLabel')}
            </label>
            <div className="flex items-center gap-2 w-full min-w-0">
              <div className="flex-1 min-w-0 flex items-center bg-gray-50 rounded-2xl px-4 h-12">
                <input
                  data-testid="otp-input"
                  aria-label="consumer-otp-input"
                  type="text"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder={loginMode === 'phone' ? t('login_codePlaceholder') : t('login_emailCodePlaceholder')}
                  className="flex-1 min-w-0 bg-transparent text-sm text-gray-900 outline-none placeholder-gray-400"
                  autoComplete="off"
                />
              </div>
              <button
                data-testid="send-code-btn"
                aria-label="consumer-send-code"
                onClick={handleSendCode}
                disabled={!currentIdentifierValid || countdown > 0 || codeLoading}
                className="flex-shrink-0 h-12 px-3 rounded-2xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer"
                style={currentIdentifierValid && countdown === 0 && !codeLoading
                  ? { background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', color: 'white' }
                  : { background: '#F3F4F6', color: '#9CA3AF' }}
              >
                {sendBtnText}
              </button>
            </div>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="mb-4 flex items-center gap-2 text-red-400 text-xs">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          )}

          {/* 协议 */}
          <div className="flex items-start gap-2 mb-5">
            <button
              type="button"
              data-testid="agree-checkbox"
              aria-label={agreed ? 'consumer-agree-checkbox-selected' : 'consumer-agree-checkbox'}
              onClick={toggleAgreement}
              className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors active:scale-95"
              style={agreed
                ? { background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', borderColor: '#FF6B9D', color: 'white' }
                : { background: 'white', borderColor: '#D1D5DB', color: 'transparent' }}
            >
              <i className="ri-check-line text-[11px] leading-none"></i>
            </button>
            {agreed && (
              <span className="sr-only" aria-label="consumer-agreement-selected">
                Agreement selected
              </span>
            )}
            <p className="text-xs text-gray-400 leading-relaxed">
              {t('login_agreePrefix')}
              <span className="text-pink-500 cursor-pointer" onClick={() => setViewAgreementKey('user_agreement')}> {t('login_userAgreement')}</span>
              {' '}{t('login_and')}{' '}
              <span className="text-pink-500 cursor-pointer" onClick={() => setViewAgreementKey('privacy_policy')}>{t('login_privacyPolicy')}</span>
            </p>
          </div>

          {/* 登录按钮 */}
          <button
            data-testid="login-btn"
            aria-label="consumer-login-submit"
            onClick={handleLogin}
            disabled={loading}
            className="w-full h-13 rounded-2xl text-white font-bold text-base cursor-pointer whitespace-nowrap transition-all active:scale-95"
            style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)', height: '52px' }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                {t('login_loggingIn')}
              </span>
            ) : (
              codeSent
                ? t('login_loginSuccess')
                : (loginMode === 'phone' ? t('login_loginBtn') : t('login_emailLoginBtn'))
            )}
          </button>

          {/* 中英文都支持切换手机号/邮箱，两种方式始终是独立账号。 */}
          <div className="mt-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-100"></div>
                <span className="text-xs text-gray-300 whitespace-nowrap">{t('login_or')}</span>
                <div className="flex-1 h-px bg-gray-100"></div>
              </div>
              <button
                data-testid="switch-login-mode"
                onClick={() => handleSwitchMode(loginMode === 'phone' ? 'email' : 'phone')}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-medium cursor-pointer transition-all active:scale-95 whitespace-nowrap"
                style={{ background: '#F8F7FF', color: '#A855F7' }}
              >
                <i className={`${loginMode === 'phone' ? 'ri-mail-line' : 'ri-smartphone-line'} text-base`}></i>
                <span>{loginMode === 'phone' ? t('login_switchEmail') : t('login_switchPhone')}</span>
              </button>
          </div>

        </div>

        {/* 底部说明 */}
        <p className="text-center text-xs text-gray-400 mt-6 px-4">
          {loginMode === 'phone' ? t('login_footerDesc') : t('login_emailFooterDesc')}
        </p>
        <p className="text-center text-xs text-gray-400 mt-2 px-4">
          {t('login_independentAccounts')}
        </p>
      </div>

      <div className="h-12"></div>

      {viewAgreementKey && config?.agreements?.[viewAgreementKey] && (
        <AgreementSheet
          agreement={config.agreements[viewAgreementKey]}
          onClose={() => setViewAgreementKey(null)}
        />
      )}
    </div>
  );
}
